// PeptideVault v6.5 — Supplements + pill packs
import { selectedPackCapacity, batchDeductionPlan } from '../services/preparation-engine.js'
import { renderSupplementIntelligenceWorkspace } from './supplement-intelligence.js'

// ══════════════════════════════════════════════════════
// RENDER: SUPPLEMENTS TAB  (placeholder — moved below)
const SUPP_FORMS = ['Capsule','Tablet','Softgel','Gummy','Powder','Liquid','Oil','Lozenge','Spray','Patch','Injection','Other']
const SUPP_CATS_DEFAULT  = [
  'Bone & Mineral Health',
  'Cardiovascular',
  'Cognitive & Neurological',
  'Digestive & Gut Health',
  'General Nutrition',
  'Hormonal & Sexual Health',
  'Inflammation & Recovery',
  'Liver & Detoxification',
  'Longevity & Cellular Health',
  'Medication',
  'Metabolic Health',
  'Muscle & Performance',
  'Research Compound',
  'Skin, Hair & Connective Tissue',
  'Sleep & Stress',
]
function suppCategories() {
  const cats = new Set(SUPP_CATS_DEFAULT)
  for (const s of (S.supplements || [])) { if (s.category) cats.add(s.category) }
  return [...cats].sort()
}
const SUPP_TIME  = { am:'AM', noon:'Noon', pm:'PM', both:'AM + PM', protocol:'Protocol' }
const SUPP_USER  = { todd:'Todd', meagan:'Meagan', both:'Both' }

function suppServingAM(s) { return +(s.timeOfDay==='both' ? (s.servingAM||s.dailyServing||1) : (s.dailyServing||1)) }
function suppServingPM(s) { return +(s.timeOfDay==='both' ? (s.servingPM||s.dailyServing||1) : (s.dailyServing||1)) }
function suppServingForTime(s, timeKey) {
  if (s.timeOfDay !== 'both') return +(s.dailyServing||1)
  return timeKey === 'pm' ? suppServingPM(s) : suppServingAM(s)
}

function suppServingForPersonTime(s, person, timeKey) {
  if (s.dosagesDifferent && s.takenBy === 'both' && ['am','noon','pm'].includes(timeKey)) {
    const override = s.userDosages?.[person]?.[timeKey]
    // Once per-person dosing is enabled, each field is authoritative. A blank
    // field means that person does not take the supplement at that time; it
    // must not silently fall back to the shared serving.
    if (override === '' || override === null || override === undefined) return 0
    if (Number.isFinite(+override)) return Math.max(0, +override)
    return 0
  }
  return suppServingForTime(s, timeKey)
}

function suppDailyBurn(s) {
  const people = s.takenBy === 'both' ? ['todd','meagan'] : [s.takenBy || 'todd']
  const times = s.timeOfDay === 'both' ? ['am','pm'] : [s.timeOfDay || 'am']
  return people.reduce((total, person) => total + times.reduce((sum, timeKey) => {
    return sum + Math.max(0, suppServingForPersonTime(s, person, timeKey))
  }, 0), 0)
}

function suppPackCapacityForPersonTime(s, person, timeKey) {
  const pillsPerPack = suppServingForPersonTime(s, person, timeKey)
  if (!(pillsPerPack > 0)) return null
  return Math.floor(Math.max(0, +(s.quantityOnHand || 0)) / pillsPerPack)
}

function touchSupplement(s, at) {
  if (s) s.updatedAt = at || now()
  return s
}

function pillPackScheduleTag(s) {
  if (s.timeOfDay === 'both') return 'AM/PM'
  if (s.timeOfDay === 'protocol') return 'Protocol'
  return SUPP_TIME[s.timeOfDay] || s.timeOfDay || 'Unscheduled'
}

function suppMatchesTime(s, timeKey) {
  return s.timeOfDay === timeKey || ((timeKey === 'am' || timeKey === 'pm') && s.timeOfDay === 'both')
}

function suppDaysSupply(s) {
  const total = Math.max(0, +(s.quantityOnHand||0) - (s.servingsDeducted||0))
  const doses = suppDailyBurn(s)
  if (!doses || !total) return null
  return Math.floor(total / doses)
}

function suppDaysColor(days) {
  if (days === null) return 'var(--text4)'
  if (days <= 7)  return 'var(--red)'
  if (days <= 21) return 'var(--yellow)'
  return 'var(--green)'
}

function suppLifecycleStatus(s) {
  const stored = String(s.status || 'active').replace('-', '_')
  if (stored === 'inactive') return 'inactive'
  if (stored === 'phase_out' || (s.depletionAction === 'discontinue' && +(s.quantityOnHand || 0) > 0)) return 'phase_out'
  if (stored === 'pending') return 'pending'
  const awaitingSource = (S.supplements || []).some(source => source.id !== s.id && source.replacedBy === s.id && (source.status || 'active') !== 'inactive' && +(source.quantityOnHand || 0) > 0)
  if (awaitingSource) return 'pending'
  if (stored === 'protocol' || s.timeOfDay === 'protocol') return 'protocol'
  return 'active'
}

function linkedProtocolStatusForSupplement(protocol, today) {
  today = today || localDateKey()
  if (!protocol || protocol.archived || protocol.supersededByImport) return 'completed'
  if (protocol.status === 'paused') return 'paused'
  const dates = []
  ;(protocol.explicitEntries || []).forEach(entry => { if (entry?.date) dates.push(entry.date) })
  ;(protocol.sourceExactSchedule || []).forEach(date => { if (date) dates.push(date) })
  if (!dates.length) { if (protocol.startDate) dates.push(protocol.startDate); if (protocol.endDate) dates.push(protocol.endDate) }
  dates.sort()
  const first = dates[0] || ''
  const last = dates[dates.length - 1] || ''
  if (last && last < today) return 'completed'
  if (first && first > today) return 'upcoming'
  return 'active'
}

function suppProtocolActivationState(s, today) {
  if (!(s.timeOfDay === 'protocol' || s.status === 'protocol')) return 'not_protocol'
  const ids = Array.isArray(s.linkedProtocolIds) ? s.linkedProtocolIds.filter(Boolean) : []
  const scheduleNames = new Set((s.linkedProtocolNames || []).map(normalizedSupplementProtocolName).filter(Boolean))
  const linkedProtocols = (S.protocols || []).filter(protocol => ids.includes(protocol.id) || [protocol.sourceColumnName, protocol.peptideName, protocol.name].some(name => scheduleNames.has(normalizedSupplementProtocolName(name))))
  if (!linkedProtocols.length) return 'manual_active'
  const states = linkedProtocols.map(protocol => linkedProtocolStatusForSupplement(protocol, today))
  if (states.includes('active')) return 'active'
  if (states.includes('paused')) return 'paused'
  if (states.includes('upcoming')) return 'upcoming'
  return 'completed'
}

function suppIsActiveForPillPacks(s, today) {
  const state = suppProtocolActivationState(s, today)
  return state === 'not_protocol' || state === 'manual_active' || state === 'active'
}

function suppIsEligibleForPillPackSection(s, person, timeKey, today) {
  const lifecycle = suppLifecycleStatus(s)
  if (!['active','phase_out','protocol'].includes(lifecycle)) return false
  if (!suppIsActiveForPillPacks(s, today)) return false

  const qty = +(s.quantityOnHand || 0)
  const depletionAction = s.depletionAction || (s.replacedBy ? 'replace' : 'keep')
  // A depleted replacement/phase-out source must never block a pack while a
  // delayed sync or legacy record is still waiting for its status transition.
  // A normal active item at zero remains visible so a genuinely missing pill
  // still prevents an incomplete pack from being created.
  if (qty <= 0 && (lifecycle === 'phase_out' || depletionAction === 'replace' || depletionAction === 'discontinue' || !!s.replacedBy)) return false

  if (!(s.takenBy === person || s.takenBy === 'both')) return false
  if (!suppMatchesTime(s, timeKey)) return false
  if (!(suppServingForPersonTime(s, person, timeKey) > 0)) return false

  // A stocked source remains authoritative until it is depleted. Do not show
  // the replacement alongside it, even if another device prematurely stored
  // the replacement as active.
  const hasStockedSource = (S.supplements || []).some(source => {
    if (source.id === s.id || source.replacedBy !== s.id) return false
    if ((source.status || 'active') === 'inactive') return false
    return +(source.quantityOnHand || 0) > 0
  })
  return !hasStockedSource
}

function normalizedSupplementProtocolName(value) {
  return String(value || '').toLowerCase().replace(/\b(round|cycle|phase)\s*\d+\b/g, '').replace(/[^a-z0-9]+/g, '')
}

function reconcileProtocolSupplementLinksFromImport(importedAt) {
  let linked = 0
  const importedProtocols = (S.protocols || []).filter(protocol => protocol.sourceType === 'excel-master-plan' && !protocol.archived && !protocol.supersededByImport)
  const sharedScheduleNames = new Set(['supplements','protocolsupplements','supplementschedule','protocolsupplementschedule'])
  const sharedSchedules = importedProtocols.filter(protocol => [protocol.sourceColumnName, protocol.peptideName, protocol.name].some(name => sharedScheduleNames.has(normalizedSupplementProtocolName(name))))
  ;(S.supplements || []).forEach(supplement => {
    const targetKeys = new Set([supplement.name, ...(supplement.linkedProtocolNames || [])].map(normalizedSupplementProtocolName).filter(Boolean))
    if (!targetKeys.size) return
    const exactMatches = importedProtocols.filter(protocol => [protocol.sourceColumnName, protocol.peptideName, protocol.name].some(name => targetKeys.has(normalizedSupplementProtocolName(name))))
    let matches = exactMatches
    // A master column literally named "Supplements" is the shared schedule for
    // protocol pills that do not have their own exact-name column. The current
    // workbook calls this "Protocol Supplements". Exact-name schedules (for
    // example SLU-PP-332) always take precedence.
    const isProtocolSupplement = supplement.timeOfDay === 'protocol' || supplement.status === 'protocol'
    const isRetired = supplement.status === 'inactive' && (+(supplement.quantityOnHand || 0) <= 0 || supplement.depletionAction === 'discontinue' || !!supplement.replacedBy)
    if (!matches.length && isProtocolSupplement && !isRetired && !(supplement.linkedProtocolNames || []).length) matches = sharedSchedules
    if (!matches.length) return
    // An exact schedule is authoritative for a stocked supplement even when it
    // was inactive only because no active master schedule was linked yet.
    if (exactMatches.length && supplement.status === 'inactive' && +(supplement.quantityOnHand || 0) > 0 && supplement.depletionAction !== 'discontinue' && !supplement.replacedBy) supplement.status = 'protocol'
    const ids = new Set(Array.isArray(supplement.linkedProtocolIds) ? supplement.linkedProtocolIds : [])
    ;(S.protocols || []).forEach(protocol => {
      if (ids.has(protocol.id) && protocol.sourceType === 'excel-master-plan' && (protocol.archived || protocol.supersededByImport)) ids.delete(protocol.id)
    })
    matches.forEach(protocol => {
      if (!ids.has(protocol.id)) { ids.add(protocol.id); linked++ }
    })
    supplement.linkedProtocolIds = [...ids]
    supplement.protocolLinksUpdatedAt = importedAt || now()
  })
  return linked
}

function suppNeedsRestockAlert(s) {
  const lifecycle = suppLifecycleStatus(s)
  const action = s.depletionAction || (s.replacedBy ? 'replace' : 'keep')
  return (lifecycle === 'active' || (lifecycle === 'protocol' && suppIsActiveForPillPacks(s))) && action === 'keep' && !s.replacedBy
}

function suppLowStock() {
  return S.supplements.filter(s => {
    if (!suppNeedsRestockAlert(s)) return false
    const days = suppDaysSupply(s)
    const thresh = +(s.lowStockDays||14)
    return +(s.quantityOnHand||0) <= 0 || (days !== null && days <= thresh)
  })
}

// ══════════════════════════════════════════════════════
// RENDER: SUPPLEMENTS TAB
// ══════════════════════════════════════════════════════
function renderSupplementsTab() {
  // Self-heal transitions created by edits, imports, or syncs before building
  // either the active or depleted supplement lists.
  if (checkSupplementTransitions({ notify: false })) save()
  // Filter
  let list = [...S.supplements]
  if (S.suppFilter !== 'all')     list = list.filter(s => s.takenBy === S.suppFilter || s.takenBy === 'both')
  if (S.suppTimeFilter !== 'all') list = list.filter(s => suppMatchesTime(s, S.suppTimeFilter))
  if ((S.suppSort||'alpha-asc') === 'alpha-asc')  list.sort((a,b) => a.name.localeCompare(b.name))
  else if (S.suppSort === 'alpha-desc') list.sort((a,b) => b.name.localeCompare(a.name))
  else if (S.suppSort === 'days-asc')  list.sort((a,b) => {
    const da = suppDaysSupply(a) ?? 99999
    const db = suppDaysSupply(b) ?? 99999
    return da - db
  })

  const sectionDefinitions = [['active','Active'], ['phase_out','Phase Out'], ['pending','Pending'], ['protocol','Protocol']]
  const sectionGroups = sectionDefinitions.map(([key,label]) => ({ key, label, items:list.filter(s => suppLifecycleStatus(s) === key) })).filter(group => group.items.length)
  const activeList = sectionGroups.flatMap(group => group.items)
  const inactiveList = list.filter(s => suppLifecycleStatus(s) === 'inactive')

  // Stats (active only)
  const lowStock = suppLowStock()
  const activeSupps = S.supplements.filter(s => ['active','phase_out','protocol'].includes(suppLifecycleStatus(s)))
  const todayAM   = activeSupps.filter(s => s.timeOfDay==='am'       || s.timeOfDay==='both').sort((a,b)=>a.name.localeCompare(b.name))
  const todayNoon = activeSupps.filter(s => s.timeOfDay==='noon').sort((a,b)=>a.name.localeCompare(b.name))
  const todayPM   = activeSupps.filter(s => s.timeOfDay==='pm'       || s.timeOfDay==='both').sort((a,b)=>a.name.localeCompare(b.name))
  const todayProt = activeSupps.filter(s => s.timeOfDay==='protocol' && suppIsActiveForPillPacks(s)).sort((a,b)=>a.name.localeCompare(b.name))

  return `
  <div style="animation:fadeIn .25s ease">
    ${renderSupplementIntelligenceWorkspace()}
    <div class="row" style="margin-bottom:16px;flex-wrap:wrap;gap:8px">
      <h2 style="font-size:20px;font-weight:700">Supplements</h2>
      <div class="spacer"></div>
      <select class="form-input form-select" style="width:auto;font-size:12px" onchange="S.suppSort=this.value;render()">
        <option value="alpha-asc"  ${(S.suppSort||'alpha-asc')==='alpha-asc' ?'selected':''}>A → Z</option>
        <option value="alpha-desc" ${S.suppSort==='alpha-desc'?'selected':''}>Z → A</option>
        <option value="days-asc"   ${S.suppSort==='days-asc'  ?'selected':''}>Days Remaining ↑</option>
      </select>
      <button class="btn btn-secondary btn-sm" onclick="downloadSuppTemplate()">${ICONS.export} Template</button>
      <button class="btn btn-secondary btn-sm" onclick="importSuppCSV()">${ICONS.plus} Import CSV</button>
      <button class="btn btn-secondary btn-sm" onclick="exportSuppCSV()">${ICONS.export} Export</button>
      ${S.supplements.length ? `<button class="btn btn-secondary btn-sm" onclick="openModal('refillSupplements')">📦 Refill</button>` : ''}
      <button class="btn btn-primary" onclick="openModal('addSupplement')">${ICONS.plus} Add Supplement</button>
    </div>

    <!-- Stats -->
    <div class="stat-grid" style="margin-bottom:16px">
      <div class="stat-card"><div class="stat-value">${S.supplements.length}</div><div class="stat-label">Total</div></div>
      <div class="stat-card"><div class="stat-value" style="color:var(--red)">${lowStock.length}</div><div class="stat-label">Low Stock</div></div>
      <div class="stat-card"><div class="stat-value">${todayAM.length}</div><div class="stat-label">AM Supplements</div></div>
      <div class="stat-card"><div class="stat-value">${todayNoon.length}</div><div class="stat-label">Noon Supplements</div></div>
      <div class="stat-card"><div class="stat-value">${todayPM.length}</div><div class="stat-label">PM Supplements</div></div>
      <div class="stat-card"><div class="stat-value">${todayProt.length}</div><div class="stat-label">Protocol</div></div>
    </div>

    <!-- Today's schedule quick view -->
    <!-- Daily Schedule - compact collapsible grid -->
    ${S.supplements.length > 0 ? `
    <div class="card" style="margin-bottom:16px">
      <div class="card-header" style="cursor:pointer" onclick="S.scheduleOpen=!S.scheduleOpen;render()">
        <div class="card-title">Daily Schedule</div>
        <div class="row" style="gap:8px;align-items:center">
          <span style="font-size:11px;color:var(--text4)">${todayAM.length} AM · ${todayNoon.length} Noon · ${todayPM.length} PM${todayProt.length?` · ${todayProt.length} Protocol`:''}</span>
          <span style="font-size:12px;color:var(--text4)">${S.scheduleOpen!==false?'▲':'▼'}</span>
        </div>
      </div>
      ${S.scheduleOpen===false ? '' : `
      <div class="card-body" style="gap:0;padding:0">
        <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid var(--border2)">
          ${[
            {label:'☀️ Todd AM',  person:'todd',   timeKey:'am',  color:'var(--blueL)',   tc:'var(--blue2)'},
            {label:'☀️ Meagan AM',person:'meagan', timeKey:'am',  color:'var(--purpleL)', tc:'var(--purpleT)'},
            {label:'🕛 Todd Noon',person:'todd',   timeKey:'noon',color:'var(--blueL)',   tc:'var(--blue2)'},
            {label:'🕛 Meagan Noon',person:'meagan',timeKey:'noon',color:'var(--purpleL)',tc:'var(--purpleT)'},
            {label:'🌙 Todd PM',  person:'todd',   timeKey:'pm',  color:'var(--blueL)',   tc:'var(--blue2)'},
            {label:'🌙 Meagan PM',person:'meagan', timeKey:'pm',  color:'var(--purpleL)', tc:'var(--purpleT)'},
          ].map((sec,i) => {
            const supps = S.supplements
              .filter(s => suppIsEligibleForPillPackSection(s, sec.person, sec.timeKey))
              .sort((a,b) => a.name.localeCompare(b.name))
            const serving = s => suppServingForPersonTime(s, sec.person, sec.timeKey)
            return `<div style="border-right:${i%2===0?'1px solid var(--border2)':'none'};${i>=2?'border-top:1px solid var(--border2)':''}">
              <div style="padding:8px 12px;background:${sec.color};font-size:11px;font-weight:700;color:${sec.tc}">${sec.label} <span style="font-weight:400;color:var(--text4)">(${supps.length})</span></div>
              <div style="padding:4px 0">
                ${supps.length===0
                  ? '<div style="padding:6px 12px;font-size:12px;color:var(--text4)">None</div>'
                  : supps.map(s => `<div style="display:flex;justify-content:space-between;align-items:center;padding:3px 12px;font-size:12px;border-bottom:1px solid var(--border2)">
                      <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:70%">${esc(s.name)}</span>
                      <span style="font-size:11px;color:var(--text3);flex-shrink:0;margin-left:4px">${serving(s)} ${s.form||'pill'}${serving(s)!==1?'s':''}</span>
                    </div>`).join('')}
              </div>
            </div>`
          }).join('')}
        </div>
        ${todayProt.length ? `
        <div>
          <div style="padding:8px 12px;background:var(--greenL);font-size:11px;font-weight:700;color:var(--greenT)">🧬 Protocol <span style="font-weight:400;color:var(--text4)">(${todayProt.length})</span></div>
          <div style="display:grid;grid-template-columns:1fr 1fr">
            ${['todd','meagan'].map((person,i) => {
              const supps = todayProt.filter(s=>s.takenBy===person||s.takenBy==='both').sort((a,b)=>a.name.localeCompare(b.name))
              return `<div style="${i===0?'border-right:1px solid var(--border2)':''}">
                <div style="padding:4px 12px 2px;font-size:10px;font-weight:700;color:var(--text4);text-transform:uppercase">${person==='todd'?'Todd':'Meagan'}</div>
                <div style="padding:0 0 4px">
                  ${supps.length===0
                    ? '<div style="padding:4px 12px;font-size:12px;color:var(--text4)">None</div>'
                    : supps.map(s=>`<div style="display:flex;justify-content:space-between;padding:3px 12px;font-size:12px;border-bottom:1px solid var(--border2)">
                        <span>${esc(s.name)}</span>
                        <span style="font-size:11px;color:var(--text3)">${s.dailyServing||1}</span>
                      </div>`).join('')}
                </div>
              </div>`
            }).join('')}
          </div>
        </div>` : ''}
      </div>`}
    </div>` : ''}

    <!-- Filters -->
    <div class="filter-bar" style="margin-bottom:12px">
      <span style="font-size:11px;font-weight:700;color:var(--text4)">Person:</span>
      ${[['all','All'],['todd','Todd'],['meagan','Meagan'],['both','Both']].map(([v,l])=>
        `<button class="filter-btn ${S.suppFilter===v?'active':''}" onclick="S.suppFilter='${v}';render()">${l}</button>`
      ).join('')}
      <span style="font-size:11px;font-weight:700;color:var(--text4);margin-left:8px">Time:</span>
      ${[['all','All'],['am','AM'],['noon','Noon'],['pm','PM'],['both','AM+PM'],['protocol','Protocol']].map(([v,l])=>
        `<button class="filter-btn ${S.suppTimeFilter===v?'active':''}" onclick="S.suppTimeFilter='${v}';render()">${l}</button>`
      ).join('')}
    </div>

    <!-- Supplement list -->
    ${list.length === 0 && S.supplements.length === 0 ? `
    <div class="card">
      <div class="empty-state" style="padding:48px">
        <div class="empty-icon">💊</div>
        <p>No supplements yet</p>
        <span>Add your supplements to track inventory and daily schedules for Todd and Meagan</span>
      </div>
    </div>` : activeList.length === 0 && inactiveList.length === 0 ? `
    <div style="color:var(--text4);font-size:13px;text-align:center;padding:32px">No supplements match the current filter</div>` : `
    ${sectionGroups.map(group => `<section style="margin-bottom:14px">
      <div style="display:flex;align-items:center;gap:8px;margin:0 0 7px 2px"><strong style="font-size:14px">${group.label}</strong><span class="badge badge-gray">${group.items.length}</span></div>
      <div class="card" style="padding:0;overflow:hidden">
      ${group.items.map((s,i) => {
        const days  = suppDaysSupply(s)
        const dColor = suppDaysColor(days)
        const doses  = suppDailyBurn(s)
        const qty = +(s.quantityOnHand||0)
        const alertEligible = suppNeedsRestockAlert(s)
        const isOut = alertEligible && qty <= 0
        const isLow = alertEligible && !isOut && days !== null && days <= +(s.lowStockDays||14)
        const lifecycle = suppLifecycleStatus(s)
        const lifecycleLabel = {active:'Active',phase_out:'Phase Out',pending:'Pending',protocol:'Protocol'}[lifecycle]
        const protocolState = suppProtocolActivationState(s)
        const protocolStateLabel = {manual_active:'Active (manual)',active:'Active by protocol',upcoming:'Upcoming',paused:'Paused',completed:'Completed'}[protocolState]
        return `
        <div style="display:grid;grid-template-columns:1fr auto;gap:12px;align-items:center;padding:12px 16px;${i>0?'border-top:1px solid var(--border2)':''};background:${isOut?'var(--redL)':isLow?'var(--yellowL)':'transparent'}">
          <div>
            <div class="row" style="gap:8px;margin-bottom:3px;flex-wrap:wrap">
              <span style="font-size:14px;font-weight:700">${esc(s.name)}</span>
              ${s.strength ? `<span class="badge badge-gray">${esc(s.strength)}</span>` : ''}
              ${s.form     ? `<span class="badge badge-gray">${esc(s.form)}</span>` : ''}
              <span class="badge ${s.takenBy==='todd'?'badge-blue':s.takenBy==='meagan'?'badge-purple':'badge-green'}">${SUPP_USER[s.takenBy]||s.takenBy}</span>
              <span class="badge badge-gray">${SUPP_TIME[s.timeOfDay]||s.timeOfDay}</span>
              ${lifecycle!=='active'?`<span class="badge badge-gray">${lifecycleLabel}</span>`:''}
              ${protocolState!=='not_protocol' ? `<span class="badge ${protocolState==='active'||protocolState==='manual_active'?'badge-green':protocolState==='upcoming'?'badge-blue':'badge-gray'}">${protocolStateLabel}</span>` : ''}
            </div>
            <div style="font-size:12px;color:var(--text3);display:flex;gap:12px;flex-wrap:wrap">
              ${s.brand ? `<span>${esc(s.brand)}</span>` : ''}
              ${s.category ? `<span>${esc(s.category)}</span>` : ''}
              ${s.subcategory ? `<span>${esc(s.subcategory)}</span>` : ''}
              <span><strong>${s.quantityOnHand||0}</strong> on hand</span>
              <span>${doses}/day</span>
              ${s.expiryDate ? `<span style="color:${new Date(s.expiryDate)<new Date()?'var(--red)':new Date(s.expiryDate)<new Date(Date.now()+90*86400000)?'var(--amber)':'var(--text4)'}">Exp: ${s.expiryDate.slice(0,10)}</span>` : ''}
              ${s.replacedBy ? (() => { const r = S.supplements.find(x=>x.id===s.replacedBy); return r ? `<span style="color:var(--blue)">→ ${esc(r.name)}${r.strength?' ('+esc(r.strength)+')':''}</span>` : '' })() : ''}
              ${(s.depletionAction||'')==='discontinue' ? '<span style="color:var(--amber)">Phase out when depleted</span>' : ''}
            </div>
          </div>
          <div style="text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:6px">
            <div style="font-size:20px;font-weight:700;color:${isOut?'var(--red)':dColor}">${isOut ? '0' : (days !== null ? days : '—')}</div>
            <div style="font-size:10px;color:var(--text4);text-transform:uppercase">${isOut?'out of stock':'days left'}</div>
            <div class="row" style="gap:4px">
              ${s.reorderUrl ? `<button class="btn btn-ghost btn-xs" title="Reorder" onclick="openPepPedia('${esc(s.reorderUrl)}')">🛒</button>` : ''}
              <button class="btn btn-ghost btn-xs" onclick="openModal('editSupplement',{suppId:'${s.id}'})">${ICONS.edit}</button>
              <button class="btn btn-danger btn-xs" onclick="deleteSupplement('${s.id}')">${ICONS.trash}</button>
            </div>
          </div>
        </div>`
      }).join('')}
      </div>
    </section>`).join('')}

    ${inactiveList.length ? `
    <div style="margin-top:20px">
      <div style="font-size:14px;font-weight:700;color:var(--text3);margin-bottom:8px;display:flex;align-items:center;gap:8px;cursor:pointer" onclick="S._showInactive=!S._showInactive;render()">
        <span style="transition:transform .2s;display:inline-block;transform:rotate(${S._showInactive?'90':'0'}deg)">${ICONS.chevron}</span>
        Inactive (${inactiveList.length})
      </div>
      ${S._showInactive ? `
      <div class="card" style="padding:0;overflow:hidden;opacity:0.7">
        ${inactiveList.map((s,i) => {
          const days  = suppDaysSupply(s)
          const dColor = suppDaysColor(days)
          const doses  = suppDailyBurn(s)
          return `
          <div style="display:grid;grid-template-columns:1fr auto;gap:12px;align-items:center;padding:12px 16px;${i>0?'border-top:1px solid var(--border2)':''}">
            <div>
              <div class="row" style="gap:8px;margin-bottom:3px;flex-wrap:wrap">
                <span style="font-size:14px;font-weight:700">${esc(s.name)}</span>
                ${s.strength ? `<span class="badge badge-gray">${esc(s.strength)}</span>` : ''}
                <span class="badge" style="background:var(--bg3);color:var(--text4)">${s.discontinuedAt?'Discontinued':'Inactive'}</span>
              </div>
              <div style="font-size:12px;color:var(--text3);display:flex;gap:12px;flex-wrap:wrap">
                ${s.brand ? `<span>${esc(s.brand)}</span>` : ''}
                <span><strong>${s.quantityOnHand||0}</strong> on hand</span>
                ${s.expiryDate ? `<span>Exp: ${s.expiryDate.slice(0,10)}</span>` : ''}
              </div>
            </div>
            <div class="row" style="gap:4px">
              <button class="btn btn-ghost btn-xs" onclick="openModal('editSupplement',{suppId:'${s.id}'})">${ICONS.edit}</button>
              <button class="btn btn-danger btn-xs" onclick="deleteSupplement('${s.id}')">${ICONS.trash}</button>
            </div>
          </div>`
        }).join('')}
      </div>` : ''}
    </div>` : ''}`}
  </div>`
}

// ══════════════════════════════════════════════════════
// RENDER: PILL PACKS TAB
// ══════════════════════════════════════════════════════
function renderPillPacksLegacy() {
  // A quantity can reach zero outside the pill-pack deduction flow (for
  // example through Edit Supplement, import, or cloud sync).
  if (checkSupplementTransitions({ notify: false })) save()
  if (!S._packCounts) S._packCounts = {}

  const people = [
    { person:'todd', label:'Todd', color:'var(--blueL)', titleColor:'var(--blue2)' },
    { person:'meagan', label:'Meagan', color:'var(--purpleL)', titleColor:'var(--purpleT)' },
  ]
  const dailyTimes = [
    { time:'AM', timeKey:'am', emoji:'☀️' },
    { time:'PM', timeKey:'pm', emoji:'🌙' },
    { time:'Noon', timeKey:'noon', emoji:'🕛' },
  ]

  function suppsForSection(person, timeKey) {
    return S.supplements
      .filter(s => suppIsEligibleForPillPackSection(s, person, timeKey))
      .sort((a,b) => a.name.localeCompare(b.name))
  }

  function sectionCard(sec) {
    const supps = suppsForSection(sec.person, sec.timeKey)
    const count = +(S._packCounts[sec.key] || 0)
    const pillsPerPack = supps.reduce((sum, item) => sum + suppServingForPersonTime(item, sec.person, sec.timeKey), 0)
    const packCapacities = supps.map(item => suppPackCapacityForPersonTime(item, sec.person, sec.timeKey)).filter(value => value !== null)
    const availablePacks = packCapacities.length ? Math.min(...packCapacities) : 0
    const totalPills = pillsPerPack * count

    return `
    <div class="card" style="padding:0;overflow:hidden">
      <div class="card-header" style="background:${sec.color}">
        <div>
          <div class="card-title" style="color:${sec.titleColor}">${sec.emoji} ${sec.time}</div>
          <div style="font-size:11px;color:var(--text3)">${supps.length} supplement${supps.length!==1?'s':''} · ${pillsPerPack} pill${pillsPerPack!==1?'s':''} per pack · ${availablePacks} available</div>
        </div>
      </div>

      <!-- Per-section pack count -->
      <div style="padding:10px 12px;background:var(--bg3);border-bottom:1px solid var(--border2);display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <div style="font-size:12px;font-weight:600;color:var(--text2);flex:1;min-width:100px">
          Packs to create:
          ${count && supps.length ? `<span style="color:var(--text3);font-weight:400"> (${totalPills} pills total)</span>` : ''}
        </div>
        <div class="row" style="gap:6px">
          <input class="form-input" id="pack-count-${sec.key}" type="number" min="1" step="1" placeholder="#"
            value="${esc(S._packCounts[sec.key]||'')}"
            style="width:70px;text-align:center;font-weight:700"
            oninput="if(!S._packCounts)S._packCounts={};S._packCounts['${sec.key}']=this.value">
          <button class="btn btn-primary btn-sm" onclick="deductSection('${sec.key}')">
            ${ICONS.check} Deduct
          </button>
        </div>
      </div>

      <!-- Supplement rows -->
      <div style="padding:0 12px">
        ${supps.length === 0
          ? '<div style="color:var(--text4);font-size:12px;padding:12px 0">No supplements</div>'
          : supps.map(s => {
              const packCapacity = suppPackCapacityForPersonTime(s, sec.person, sec.timeKey)
              const dColor = suppDaysColor(packCapacity)
              return `<div style="display:grid;grid-template-columns:1fr auto auto;gap:8px;align-items:center;padding:8px 0;border-bottom:1px solid var(--border2)">
                <div>
                  <div style="font-size:13px;font-weight:600">${esc(s.name)}</div>
                  <div style="display:flex;align-items:center;gap:5px;flex-wrap:wrap;margin-top:2px;font-size:11px;color:var(--text3)">
                    ${s.brand ? `<span>${esc(s.brand)}</span>` : ''}
                    <span class="badge badge-gray" style="font-size:9px">${esc(pillPackScheduleTag(s))}</span>
                    ${s.strength ? `<span>${esc(s.strength)}</span>` : ''}
                  </div>
                </div>
                <div style="text-align:center;min-width:40px">
                  <div style="font-size:16px;font-weight:700">${suppServingForPersonTime(s, sec.person, sec.timeKey)}</div>
                  <div style="font-size:9px;color:var(--text4)">${s.form||'pill'}${suppServingForPersonTime(s,sec.person,sec.timeKey)!==1?'s':''}</div>
                </div>
                <div style="text-align:right;min-width:36px">
                  <div style="font-size:12px;font-weight:700;color:${dColor}">${packCapacity!==null?packCapacity:'—'}</div>
                  <div style="font-size:9px;color:var(--text4)">packs</div>
                </div>
              </div>`
            }).join('')}
      </div>
    </div>`
  }

  function userSection(person) {
    const dailySections = dailyTimes.map(time => ({
      ...person,
      ...time,
      key: `${person.person}-${time.timeKey}`,
    }))
    const protocolSection = {
      ...person,
      key: `${person.person}-protocol`,
      time: 'Protocol',
      timeKey: 'protocol',
      color: 'var(--greenL)',
      titleColor: 'var(--greenT)',
      emoji: '🧬',
    }
    const total = [...dailySections, protocolSection]
      .reduce((sum, sec) => sum + suppsForSection(sec.person, sec.timeKey).length, 0)
    return `<section class="card pill-pack-user-section" style="padding:14px;overflow:visible">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">
        <div style="width:34px;height:34px;border-radius:50%;display:grid;place-items:center;background:${person.color};color:${person.titleColor};font-weight:800">${esc(person.label.slice(0,1))}</div>
        <div>
          <div style="font-size:17px;font-weight:800;color:${person.titleColor}">${esc(person.label)} Pill Packs</div>
          <div style="font-size:11px;color:var(--text4)">${total} scheduled supplement${total!==1?'s':''}</div>
        </div>
      </div>
      <div class="pill-pack-time-grid">
        ${sectionCard(dailySections[0])}
        ${sectionCard(dailySections[1])}
        <div class="pill-pack-noon-protocol-stack">
          ${sectionCard(dailySections[2])}
          ${sectionCard(protocolSection)}
        </div>
      </div>
    </section>`
  }

  return `
  <div style="animation:fadeIn .25s ease">
    <div class="row" style="margin-bottom:16px">
      <div>
        <h2 style="font-size:20px;font-weight:700">Pill Packs</h2>
        <div style="font-size:13px;color:var(--text3)">AM, PM, Noon, and Protocol packs grouped by person</div>
      </div>
    </div>

    <div class="pill-pack-users">
      ${people.map(userSection).join('')}
    </div>

    ${!S.supplements.length ? `
    <div class="card" style="margin-top:12px">
      <div class="empty-state" style="padding:32px">
        <div class="empty-icon">💊</div>
        <p>No supplements configured</p>
        <span>Add supplements in the Supplements tab first</span>
      </div>
    </div>` : ''}
  </div>`
}

function pillPackSelectedSlots() {
  const slots = Array.isArray(S._pillPackSlots) ? S._pillPackSlots.filter(slot => ['am','noon','pm','protocol'].includes(slot)) : []
  return slots.length ? slots : ['am']
}

function pillPackEligibleSupplements(person, slots) {
  const seen = new Set()
  return (S.supplements || []).filter(supplement => slots.some(slot => suppIsEligibleForPillPackSection(supplement,person,slot))).filter(supplement => {
    if (seen.has(supplement.id)) return false
    seen.add(supplement.id); return true
  }).sort((a,b) => String(a.name||'').localeCompare(String(b.name||'')))
}

function pillPackScheduleFingerprint(person, slots, supplements) {
  return JSON.stringify({ person, slots:[...slots].sort(), rows:supplements.map(s => ({ id:s.id, updatedAt:s.updatedAt||'', quantityOnHand:s.quantityOnHand, timeOfDay:s.timeOfDay, takenBy:s.takenBy, dailyServing:s.dailyServing, servingAM:s.servingAM, servingPM:s.servingPM, userDosages:s.userDosages||null })) })
}

function resetPillPackChecks() { S._pillPackCheckedIds=[] }
function pillPackCheckedIds() { return new Set(Array.isArray(S._pillPackCheckedIds)?S._pillPackCheckedIds:[]) }
function setPillPackPerson(value) { S._pillPackPerson=value||'todd'; S._activePreparationBatchId=''; resetPillPackChecks(); render() }
function togglePillPackSlot(slot) {
  const current=new Set(pillPackSelectedSlots())
  if (current.has(slot) && current.size>1) current.delete(slot); else current.add(slot)
  S._pillPackSlots=[...current]; S._activePreparationBatchId=''; resetPillPackChecks(); render()
}
function setPillPackView(value) { S._pillPackView=value==='all'?'all':'remaining'; render() }
function setPillPackBatchSize(value) { S._pillPackBatchSize=Math.max(1,Math.floor(Number(value)||1)); resetPillPackChecks(); render() }

function togglePillPackPreviewItem(supplementId) {
  const checked=pillPackCheckedIds()
  if (checked.has(supplementId)) checked.delete(supplementId); else checked.add(supplementId)
  S._pillPackCheckedIds=[...checked]
  render()
}

async function recordCreatedPillPacks() {
  const person=S._pillPackPerson||'todd',slots=pillPackSelectedSlots(),supplements=pillPackEligibleSupplements(person,slots)
  const count=Math.max(1,Math.floor(Number(S._pillPackBatchSize)||1))
  if (!supplements.length) { showToast('No supplements match this pack','error'); return }
  const capacity=selectedPackCapacity(supplements,person,slots),plan=batchDeductionPlan(capacity,count)
  if (!plan.ok) { showToast(plan.reason,'error'); return }
  const checked=pillPackCheckedIds(),missing=capacity.rows.filter(row=>!checked.has(row.supplementId))
  if (missing.length) { showToast(`Check every packed supplement first — ${missing.length} remaining`,'error'); return }
  for (const deduction of plan.deductions) {
    const supplement=(S.supplements||[]).find(item=>item.id===deduction.supplementId)
    if (!supplement || Number(supplement.quantityOnHand)<deduction.quantity) { showToast('Stock changed while these packs were being prepared. Review the list.','error'); return }
  }
  const totalPills=plan.deductions.reduce((sum,item)=>sum+item.quantity,0)
  const slotLabel=slots.map(slot=>slot==='protocol'?'Protocol':slot.toUpperCase()).join(' + ')
  if (!await asyncConfirm(`Record ${count} completed ${person.replace(/^./,c=>c.toUpperCase())} ${slotLabel} pack${count===1?'':'s'}?\n${totalPills} pills will be removed from inventory.`)) return
  const completedAt=now()
  plan.deductions.forEach(deduction=>{ const supplement=S.supplements.find(item=>item.id===deduction.supplementId); supplement.quantityOnHand=Number(supplement.quantityOnHand)-deduction.quantity; touchSupplement(supplement,completedAt) })
  S.preparationBatches=Array.isArray(S.preparationBatches)?S.preparationBatches:[]
  S.preparationBatches.push({ id:'pack_'+uid(),type:'pill_pack',status:'completed',person,slots:[...slots],packCount:count,createdAt:completedAt,completedAt,updatedAt:completedAt,items:capacity.rows.map(row=>({ supplementId:row.supplementId,name:row.name,pillsPerPack:row.pillsPerPack,availableAtStart:row.available,required:row.pillsPerPack*count,added:true })),deductions:plan.deductions.map(item=>({...item})),undoAvailable:true })
  checkSupplementTransitions({notify:false})
  S._pillPackBatchSize=1; resetPillPackChecks(); S._activePreparationBatchId=''
  await save(); render(); showToast(`✅ ${count} completed pack${count===1?'':'s'} recorded — ${totalPills} pills deducted`)
}

async function savePillPackDraft() {
  const person=S._pillPackPerson||'todd',slots=pillPackSelectedSlots(),supplements=pillPackEligibleSupplements(person,slots)
  if (!supplements.length) { showToast('No supplements match this pack','error'); return }
  const capacity=selectedPackCapacity(supplements,person,slots),count=Math.max(1,Math.floor(Number(S._pillPackBatchSize)||1)),plan=batchDeductionPlan(capacity,count)
  if (!plan.ok) { showToast(plan.reason,'error'); return }
  S.preparationBatches=Array.isArray(S.preparationBatches)?S.preparationBatches:[]
  let batch=(S.preparationBatches||[]).find(item=>item.id===S._activePreparationBatchId&&item.status==='draft')
  const snapshot=capacity.rows.map(row=>({ supplementId:row.supplementId,name:row.name,pillsPerPack:row.pillsPerPack,availableAtStart:row.available,required:row.pillsPerPack*count,added:false }))
  if (!batch) {
    batch={ id:'pack_'+uid(),type:'pill_pack',status:'draft',person,slots:[...slots],packCount:count,createdAt:now(),updatedAt:now(),items:snapshot,scheduleFingerprint:pillPackScheduleFingerprint(person,slots,supplements) }
    S.preparationBatches.push(batch); S._activePreparationBatchId=batch.id
  } else {
    batch.person=person; batch.slots=[...slots]; batch.packCount=count; batch.items=snapshot; batch.updatedAt=now(); batch.scheduleFingerprint=pillPackScheduleFingerprint(person,slots,supplements)
  }
  await save(); render(); showToast('Pack checklist saved')
}

async function resumePillPackDraft(id) {
  const batch=(S.preparationBatches||[]).find(item=>item.id===id&&item.status==='draft')
  if (!batch) return
  S._activePreparationBatchId=batch.id; S._pillPackPerson=batch.person; S._pillPackSlots=[...(batch.slots||[])]; S._pillPackBatchSize=batch.packCount; render()
}

async function togglePillPackItem(batchId,supplementId) {
  const batch=(S.preparationBatches||[]).find(item=>item.id===batchId&&item.status==='draft')
  const item=batch&&batch.items.find(row=>row.supplementId===supplementId)
  if (!item) return
  item.added=!item.added; batch.updatedAt=now(); await save(); render()
}

async function finalizePillPackBatch(batchId) {
  const batch=(S.preparationBatches||[]).find(item=>item.id===batchId)
  if (!batch || batch.status!=='draft') { showToast('This batch was already finalized'); return }
  const supplements=pillPackEligibleSupplements(batch.person,batch.slots||[])
  if (pillPackScheduleFingerprint(batch.person,batch.slots||[],supplements)!==batch.scheduleFingerprint) { showToast('Pack schedule changed after this checklist was created. Start a new checklist.','error'); return }
  if (!(batch.items||[]).length || batch.items.some(item=>!item.added)) { showToast('Check every item added to the pack before finalizing','error'); return }
  const capacity=selectedPackCapacity(supplements,batch.person,batch.slots||[]),plan=batchDeductionPlan(capacity,batch.packCount)
  if (!plan.ok) { showToast(plan.reason,'error'); return }
  for (const deduction of plan.deductions) {
    const supplement=(S.supplements||[]).find(item=>item.id===deduction.supplementId)
    if (!supplement || Number(supplement.quantityOnHand)<deduction.quantity) { showToast('Stock changed while this pack was being prepared. Review the checklist.','error'); return }
  }
  const completedAt=now()
  plan.deductions.forEach(deduction=>{ const supplement=S.supplements.find(item=>item.id===deduction.supplementId); supplement.quantityOnHand=Number(supplement.quantityOnHand)-deduction.quantity; touchSupplement(supplement,completedAt) })
  batch.status='completed'; batch.completedAt=completedAt; batch.updatedAt=completedAt; batch.deductions=plan.deductions.map(item=>({...item})); batch.undoAvailable=true
  checkSupplementTransitions({notify:false}); S._activePreparationBatchId=''; await save(); render(); showToast('Pack batch finalized — stock deducted once')
}

async function undoPillPackBatch(batchId) {
  const batch=(S.preparationBatches||[]).find(item=>item.id===batchId&&item.status==='completed'&&item.undoAvailable)
  if (!batch) { showToast('This batch cannot be undone','error'); return }
  const at=now()
  ;(batch.deductions||[]).forEach(deduction=>{ const supplement=(S.supplements||[]).find(item=>item.id===deduction.supplementId); if(supplement){supplement.quantityOnHand=Number(supplement.quantityOnHand||0)+Number(deduction.quantity||0);touchSupplement(supplement,at)} })
  batch.status='corrected'; batch.correctedAt=at; batch.updatedAt=at; batch.undoAvailable=false
  await save(); render(); showToast('Batch correction restored the deducted stock')
}

function startNewPillPackBatch() { S._activePreparationBatchId=''; S._pillPackBatchSize=1; render() }

function renderPillPacksTab() {
  if (checkSupplementTransitions({notify:false})) save()
  const person=S._pillPackPerson||'todd',slots=pillPackSelectedSlots(),supplements=pillPackEligibleSupplements(person,slots)
  const capacity=selectedPackCapacity(supplements,person,slots),count=Math.max(1,Math.floor(Number(S._pillPackBatchSize)||1))
  const history=(S.preparationBatches||[]).filter(item=>item.type==='pill_pack'&&item.status!=='draft').sort((a,b)=>String(b.completedAt||b.correctedAt||'').localeCompare(String(a.completedAt||a.correctedAt||''))).slice(0,8)
  const checked=pillPackCheckedIds(),displayRows=capacity.rows.map(row=>{
    const supplement=supplements.find(item=>item.id===row.supplementId)
    const scheduleLabel=supplement.timeOfDay==='both'?'AM/PM (Both)':pillPackScheduleTag(supplement)
    return {...row,scheduleLabel,brand:supplement.brand||'',required:row.pillsPerPack*count,added:checked.has(row.supplementId)}
  })
  const checkedCount=displayRows.filter(row=>row.added).length
  return `<div class="tab-content v9-pack-page"><header class="v9-pack-header"><div><span class="v9-kicker">Preparation</span><h1>Pill Packs</h1><p>Select the person and pack slots, check each supplement as it is packed, then record the completed packs.</p></div></header>
  <section class="v9-surface v9-pack-controls"><label>Person<select class="form-input" onchange="setPillPackPerson(this.value)"><option value="todd" ${person==='todd'?'selected':''}>Todd</option><option value="meagan" ${person==='meagan'?'selected':''}>Meagan</option></select></label><fieldset><legend>Pack slots</legend>${['am','noon','pm','protocol'].map(slot=>`<button class="v9-slot ${slots.includes(slot)?'active':''}" onclick="togglePillPackSlot('${slot}')">${slot==='protocol'?'Protocol':slot.toUpperCase()}</button>`).join('')}</fieldset></section>
  <section class="v9-surface v9-pack-capacity"><div><small>Maximum complete packs</small><b>${capacity.maxPacks==null?'Not assessed':capacity.maxPacks}</b><span>${capacity.incomplete?'Unknown stock must be reviewed':capacity.limitingSupplementIds.length?'Limited by '+capacity.rows.filter(row=>capacity.limitingSupplementIds.includes(row.supplementId)).map(row=>esc(row.name)).join(', '):supplements.length?'No limiting item':'No matching supplements'}</span></div><div class="v9-packs-created"><label><small>Number created</small><input class="form-input" type="number" min="1" value="${count}" onchange="setPillPackBatchSize(this.value)"></label><button class="btn btn-primary" onclick="recordCreatedPillPacks()">Pill Packs Created</button></div></section>
  <section class="v9-surface"><header class="v9-inline-header"><div><h3>Packing checklist</h3><p>${checkedCount} of ${displayRows.length} supplements packed · ${capacity.rows.reduce((sum,row)=>sum+row.pillsPerPack*count,0)} pills for this batch</p></div></header><div class="v9-pack-checklist">${displayRows.length?displayRows.map(row=>`<button class="${row.added?'done':''}" onclick="togglePillPackPreviewItem('${row.supplementId}')"><i>${row.added?'✓':'□'}</i><span><b>${esc(row.name)} ${row.brand?`<span style="font-weight:400">· ${esc(row.brand)}</span>`:''} <span class="badge badge-gray" style="display:inline-block;margin-left:6px">${esc(row.scheduleLabel)}</span></b><small>${row.pillsPerPack} per pack · ${row.required} pills for ${count} pack${count===1?'':'s'}</small></span><em>${row.available==null?'Stock unknown':row.available+' available'}</em></button>`).join(''):'<div class="v9-empty-inline">No supplements match the selected person and slots.</div>'}</div><footer><span>Inventory changes only when <b>Pill Packs Created</b> is confirmed.</span></footer></section>
  ${history.length?`<details class="v9-surface v9-pack-history"><summary>Preparation history (${history.length})</summary>${history.map(batch=>`<div><span><b>${esc((batch.person||'').replace(/^./,c=>c.toUpperCase()))} · ${(batch.slots||[]).map(slot=>slot.toUpperCase()).join(' + ')}</b><small>${batch.packCount} packs · ${esc(fmt(batch.completedAt||batch.correctedAt))}</small></span><em>${esc(batch.status)}</em>${batch.undoAvailable?`<button class="btn btn-secondary btn-sm" onclick="undoPillPackBatch('${batch.id}')">Undo deduction</button>`:''}</div>`).join('')}</details>`:''}
  <details class="v9-surface"><summary>View all traditional pack sections</summary>${renderPillPacksLegacy()}</details></div>`
}

function checkSupplementTransitions(options = {}) {
  // Apply the configured end-of-stock action. Legacy replacement links retain
  // their original replace behavior even before depletionAction was introduced.
  const notify = options.notify !== false
  let changed = 0
  for (const s of S.supplements) {
    const qty = +(s.quantityOnHand||0)
    if (qty > 0) continue
    const storedStatus = String(s.status || 'active').replace('-', '_')
    const action = s.replacedBy ? 'replace' : (storedStatus === 'phase_out' ? 'discontinue' : (s.depletionAction || 'keep'))
    if (action === 'discontinue') {
      if (s.status === 'inactive' && s.discontinuedAt) continue
      const changedAt = now()
      s.status = 'inactive'
      s.discontinuedAt = s.discontinuedAt || changedAt
      touchSupplement(s, changedAt)
      changed++
      if (notify) showToast(`Discontinued: ${s.name} (stock depleted)`)
      logChange('supplement', s.id, 'phase-out', `${s.name} depleted and was discontinued`)
    } else if (action === 'replace' && s.replacedBy) {
      const replacement = S.supplements.find(r => r.id === s.replacedBy)
      if (replacement) {
        const needsSourceUpdate = s.status !== 'inactive'
        const needsReplacementUpdate = replacement.status !== 'active'
        if (!needsSourceUpdate && !needsReplacementUpdate) continue
        const changedAt = now()
        s.status = 'inactive'
        replacement.status = 'active'
        touchSupplement(s, changedAt)
        touchSupplement(replacement, changedAt)
        changed++
        if (notify) showToast(`Transitioned: ${s.name} → ${replacement.name}`)
        logChange('supplement', s.id, 'transition', `${s.name} depleted → activated ${replacement.name}`)
      }
    }
  }
  return changed
}

async function deductSection(sectionKey) {
  try {
  if (!S._packCounts) S._packCounts = {}
  // Read directly from the input element
  const input = document.getElementById('pack-count-' + sectionKey)
  const packCount = +(input?.value || S._packCounts[sectionKey] || 0)
  if (!packCount || packCount < 1) { showToast('Enter number of packs to create'); return }

  // Determine person and timeKey from section key
  const [personPart, ...timeParts] = sectionKey.split('-')
  const timeKey = timeParts.join('-') // 'am', 'pm', 'protocol'

  // Find supplements in this specific section
  const supps = S.supplements.filter(s => suppIsEligibleForPillPackSection(s, personPart, timeKey))

  if (!supps.length) { showToast('No supplements in this section'); return }

  const availablePacks = Math.min(...supps.map(s => suppPackCapacityForPersonTime(s, personPart, timeKey)).filter(value => value !== null))
  if (packCount > availablePacks) { showToast(`Only ${availablePacks} ${sectionKey.replace('-',' ')} pack${availablePacks!==1?'s':''} can be created from current stock`); return }

  const totalPills = supps.reduce((sum, item) => sum + suppServingForPersonTime(item, personPart, timeKey), 0) * packCount
  const sectionLabel = sectionKey.replace('-',' ').replace('am','AM').replace('pm','PM').replace('protocol','Protocol')
    .replace('todd','Todd').replace('meagan','Meagan')

  if (!await asyncConfirm(`Deduct ${packCount} pack${packCount!==1?'s':''} from ${sectionLabel}?
${totalPills} total pills will be removed.`)) return

  let deducted = 0
  supps.forEach(s => {
    const pills = packCount * suppServingForPersonTime(s, personPart, timeKey)
    s.quantityOnHand = Math.max(0, +(s.quantityOnHand||0) - pills)
    touchSupplement(s)
    deducted += pills
  })

  S._packCounts[sectionKey] = ''
  checkSupplementTransitions()
  save(); render()
  showToast(`✅ ${packCount} pack${packCount!==1?'s':''} created — ${deducted} pills deducted`)
  } catch(err) { showToast('❌ Deduct error: ' + err.message); console.error('deductSection error:', err) }
}

export {
  SUPP_FORMS,
  SUPP_CATS_DEFAULT,
  suppCategories,
  SUPP_TIME,
  SUPP_USER,
  suppServingAM,
  suppServingPM,
  suppServingForTime,
  suppServingForPersonTime,
  suppDailyBurn,
  suppPackCapacityForPersonTime,
  touchSupplement,
  pillPackScheduleTag,
  suppMatchesTime,
  suppDaysSupply,
  suppDaysColor,
  suppLifecycleStatus,
  linkedProtocolStatusForSupplement,
  suppProtocolActivationState,
  suppIsActiveForPillPacks,
  suppIsEligibleForPillPackSection,
  reconcileProtocolSupplementLinksFromImport,
  suppNeedsRestockAlert,
  suppLowStock,
  renderSupplementsTab,
  renderPillPacksTab,
  checkSupplementTransitions,
  deductSection,
  setPillPackPerson,
  togglePillPackSlot,
  setPillPackView,
  setPillPackBatchSize,
  togglePillPackPreviewItem,
  recordCreatedPillPacks,
  savePillPackDraft,
  resumePillPackDraft,
  togglePillPackItem,
  finalizePillPackBatch,
  undoPillPackBatch,
  startNewPillPackBatch
}
